import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import api, { imgUrl, apiErr } from "@/lib/api";
import { TouchInput } from "@/components/VirtualKeyboard";
import { toast } from "sonner";
import { Minus, Plus, Trash2, ShoppingCart, CheckCircle2, Keyboard } from "lucide-react";

export function OrderDrawer({ open, onClose }) {
  const { t, cart, updateCartQty, removeFromCart, cartTotal, clearCart } = useApp();
  const [notes, setNotes] = useState("");
  const [customer, setCustomer] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const confirm = async () => {
    setSubmitting(true);
    try {
      const items = cart.map(({ product_id, name, size, quantity, price, subtotal }) => ({
        product_id, name, size, quantity, price, subtotal,
      }));
      await api.post("/orders", { items, notes, customer_info: customer });
      toast.success(t("orderSent"), { icon: <CheckCircle2 className="h-5 w-5" /> });
      clearCart();
      setNotes("");
      setCustomer("");
      onClose();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        data-testid="order-drawer"
        className="w-full sm:max-w-md flex flex-col p-0"
      >
        <SheetHeader className="px-5 py-4 border-b">
          <SheetTitle className="font-display flex items-center gap-2 text-xl">
            <ShoppingCart className="h-6 w-6 text-primary" /> {t("orderSummary")}
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto kiosk-scroll px-5 py-4 space-y-3">
          {cart.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-muted-foreground py-16">
              <ShoppingCart className="h-14 w-14 mb-3 opacity-40" />
              <p>{t("empty")}</p>
            </div>
          ) : (
            cart.map((item, i) => (
              <div key={i} data-testid={`cart-item-${i}`} className="flex gap-3 rounded-xl border bg-card p-3">
                <div className="h-16 w-16 rounded-lg bg-muted overflow-hidden flex-shrink-0">
                  {item.image && <img src={imgUrl(item.image)} alt="" className="h-full w-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm leading-tight line-clamp-1">{item.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {t("size")}: {item.size} · ${item.price.toFixed(2)}
                  </div>
                  <div className="mt-1.5 flex items-center gap-2">
                    <button
                      data-testid={`cart-minus-${i}`}
                      onClick={() => updateCartQty(i, -1)}
                      className="h-8 w-8 rounded-lg border flex items-center justify-center"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="font-mono font-bold w-6 text-center">{item.quantity}</span>
                    <button
                      data-testid={`cart-plus-${i}`}
                      onClick={() => updateCartQty(i, 1)}
                      className="h-8 w-8 rounded-lg border flex items-center justify-center"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                    <span className="ml-auto font-mono font-bold text-primary">${item.subtotal.toFixed(2)}</span>
                    <button
                      data-testid={`cart-remove-${i}`}
                      onClick={() => removeFromCart(i)}
                      className="h-8 w-8 rounded-lg text-destructive flex items-center justify-center"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}

          {cart.length > 0 && (
            <div className="space-y-3 pt-2">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t("customerInfo")}
                </label>
                <TouchInput testId="order-customer-input" value={customer} onChange={setCustomer} placeholder={t("customerInfoPh")} className="mt-1" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                  {t("additionalInfo")}
                </label>
                <TouchInput testId="order-notes-input" value={notes} onChange={setNotes} placeholder={t("additionalInfoPh")} className="mt-1" />
              </div>
            </div>
          )}
        </div>

        {cart.length > 0 && (
          <div className="border-t px-5 py-4 space-y-3 bg-card">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t("total")}</span>
              <span data-testid="order-summary-total-amount" className="font-mono font-extrabold text-2xl text-primary">
                ${cartTotal.toFixed(2)}
              </span>
            </div>
            <Button
              data-testid="confirm-order-submit-button"
              onClick={confirm}
              disabled={submitting}
              className="w-full min-h-[56px] rounded-xl text-base font-bold"
            >
              {submitting ? "..." : t("confirmOrder")}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
